import { useSettingsStore, DEFAULT_SETTINGS } from '@/stores';
import { TemplateEditor, type TemplateVariableOption } from './TemplateEditor';
import { getCurrencySymbol } from '@/utils/currency';
import {
  CalendarClock, Bell, Sparkles, Car, Scissors, XCircle,
  UserX, Star, RotateCw, HeartHandshake
} from 'lucide-react';
import type { CompletedMessageMode } from '@/types/settings';

export function AppointmentsConfig() {
  const settings = useSettingsStore();
  const sym = getCurrencySymbol(settings.currencyCode);

  const appointmentVars: TemplateVariableOption[] = [
    // Cliente
    { token: 'cliente', label: 'Nombre cliente (inteligente)', category: 'cliente' },
    { token: 'primer_nombre', label: 'Primer nombre (exacto)', category: 'cliente' },
    { token: 'nombre_completo', label: 'Nombre completo', category: 'cliente' },
    { token: 'apellido', label: 'Apellidos', category: 'cliente' },

    // Cita & Especialista
    { token: 'fecha_hora', label: 'Fecha y hora', category: 'cita' },
    { token: 'fecha', label: 'Solo fecha', category: 'cita' },
    { token: 'hora', label: 'Solo hora', category: 'cita' },
    { token: 'servicios', label: 'Servicio', category: 'cita' },
    { token: 'de_servicios', label: 'de Servicio (conector)', category: 'cita' },
    { token: 'profesional', label: 'Especialista / Profesional', category: 'cita' },
    { token: 'sede', label: 'Sede / Salón', category: 'cita' },

    // Precios
    { token: 'precio_bloque', label: 'Bloque precio inteligente', category: 'precios' },
    { token: 'precio', label: 'Texto precio en línea', category: 'precios' },
    { token: 'monto_total', label: `Monto (${sym})`, category: 'precios' },
    { token: 'monto', label: 'Monto solo en números (sin símbolo)', category: 'precios' },
    { token: 'monto_bs', label: 'Monto en Bs.', category: 'precios' },

    // General
    { token: 'saludo', label: 'Saludo contextual', category: 'general' },
    { token: 'negocio', label: 'Nombre negocio', category: 'general' },
    { token: 'firma', label: 'Texto de cierre', category: 'general' },
    { token: 'link_resena', label: 'Enlace reseña', category: 'general' },
  ];

  return (
    <div className="space-y-4">
      {/* Estrategia de Citas Completadas */}
      <div className="bg-surface border border-border rounded-xl p-4 space-y-3 shadow-xs">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center shrink-0">
            <CalendarClock size={16} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-text-primary">Estrategia para Citas Completadas</h3>
            <p className="text-[11px] text-text-muted">Determina qué mensaje sugerir cuando la cita está en estado COMPLETADA</p>
          </div>
        </div>

        <div className="pt-1">
          <select
            value={settings.completedMessageMode}
            onChange={(e) => settings.setSetting('completedMessageMode', e.target.value as CompletedMessageMode)}
            className="w-full px-3 py-2.5 bg-surface-secondary border border-border rounded-xl text-xs text-text-primary cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
          >
            <option value="auto">Automático (Pide reseña si no tiene, o agradece visita)</option>
            <option value="review_request">Solicitar Reseña (Pedir feedback y calificación)</option>
            <option value="rebook">Reagendar Próxima Visita (Recordar volver pronto)</option>
            <option value="loyalty">Agradecimiento General (Fidelización)</option>
          </select>
        </div>
      </div>

      {/* Ciclo de Vida de Citas (9 Plantillas en Acordeón) */}
      <div className="space-y-3">
        <div className="flex items-center justify-between px-1">
          <h3 className="text-xs font-bold uppercase tracking-wider text-text-muted">
            Plantillas del Ciclo de Vida (9 Estados)
          </h3>
          <span className="text-[10px] text-text-muted">Haz clic en cada sección para editar</span>
        </div>

        {/* 1. Recordatorio */}
        <TemplateEditor
          title="Recordatorio de Cita (Confirmada / Por Confirmar)"
          template={settings.templateReminder}
          defaultTemplate={DEFAULT_SETTINGS.templateReminder}
          onChange={(v) => settings.setSetting('templateReminder', v)}
          variables={appointmentVars}
          collapsible
          defaultExpanded={true}
          previewType="appointment"
          icon={<Bell size={15} />}
        />

        {/* 2. Cita Nueva */}
        <TemplateEditor
          title="Cita Nueva / Reserva Reciente"
          template={settings.templateNew}
          defaultTemplate={DEFAULT_SETTINGS.templateNew}
          onChange={(v) => settings.setSetting('templateNew', v)}
          variables={appointmentVars}
          collapsible
          defaultExpanded={false}
          previewType="appointment"
          icon={<Sparkles size={15} />}
        />

        {/* 3. Llegada Confirmada (Arrived) */}
        <TemplateEditor
          title="Cliente en Salón / Llegada Confirmada"
          template={settings.templateArrived}
          defaultTemplate={DEFAULT_SETTINGS.templateArrived}
          onChange={(v) => settings.setSetting('templateArrived', v)}
          variables={appointmentVars}
          collapsible
          defaultExpanded={false}
          previewType="appointment"
          icon={<Car size={15} />}
        />

        {/* 4. Servicio Iniciado (Started) */}
        <TemplateEditor
          title="Servicio en Proceso / Iniciado"
          template={settings.templateStarted}
          defaultTemplate={DEFAULT_SETTINGS.templateStarted}
          onChange={(v) => settings.setSetting('templateStarted', v)}
          variables={appointmentVars}
          collapsible
          defaultExpanded={false}
          previewType="appointment"
          icon={<Scissors size={15} />}
        />

        {/* 5. Cita Cancelada */}
        <TemplateEditor
          title="Cita Cancelada"
          template={settings.templateCancelled}
          defaultTemplate={DEFAULT_SETTINGS.templateCancelled}
          onChange={(v) => settings.setSetting('templateCancelled', v)}
          variables={appointmentVars}
          collapsible
          defaultExpanded={false}
          previewType="appointment"
          icon={<XCircle size={15} className="text-brand-danger" />}
        />

        {/* 6. No Asistió (Inasistencia) */}
        <TemplateEditor
          title="Inasistencia (Cliente no asistió)"
          template={settings.templateNoShow}
          defaultTemplate={DEFAULT_SETTINGS.templateNoShow}
          onChange={(v) => settings.setSetting('templateNoShow', v)}
          variables={appointmentVars}
          collapsible
          defaultExpanded={false}
          previewType="appointment"
          icon={<UserX size={15} className="text-brand-danger" />}
        />

        {/* 7. Completada: Reseña */}
        <TemplateEditor
          title="Completada: Solicitud de Reseña"
          template={settings.templateCompletedReview}
          defaultTemplate={DEFAULT_SETTINGS.templateCompletedReview}
          onChange={(v) => settings.setSetting('templateCompletedReview', v)}
          variables={appointmentVars}
          collapsible
          defaultExpanded={false}
          previewType="appointment"
          icon={<Star size={15} className="text-amber-500" />}
        />

        {/* 8. Completada: Reagendar */}
        <TemplateEditor
          title="Completada: Reagendar Próxima Visita"
          template={settings.templateCompletedRebook}
          defaultTemplate={DEFAULT_SETTINGS.templateCompletedRebook}
          onChange={(v) => settings.setSetting('templateCompletedRebook', v)}
          variables={appointmentVars}
          collapsible
          defaultExpanded={false}
          previewType="appointment"
          icon={<RotateCw size={15} className="text-brand-primary" />}
        />

        {/* 9. Completada: Fidelización General */}
        <TemplateEditor
          title="Completada: Agradecimiento General"
          template={settings.templateCompletedGeneral}
          defaultTemplate={DEFAULT_SETTINGS.templateCompletedGeneral}
          onChange={(v) => settings.setSetting('templateCompletedGeneral', v)}
          variables={appointmentVars}
          collapsible
          defaultExpanded={false}
          previewType="appointment"
          icon={<HeartHandshake size={15} className="text-state-confirmed" />}
        />
      </div>
    </div>
  );
}
